import {
  encryptPayload,
  looksEncrypted,
  payloadEncryptionEnabled,
  decryptPayload,
} from './payload-crypto'
import type {
  BondTerm,
  ExitQuote,
  PositionValuation,
  RbsZone,
  RoiPoint,
  TurbinePreview,
} from './protocol'

/**
 * Typed API client.
 *
 * The single rule: every monetary field arrives as a DECIMAL STRING and is converted
 * to `bigint` here, at the boundary. Nothing downstream ever sees a JSON number for a
 * token amount — a uint256 does not survive an IEEE-754 double, and a portfolio that
 * is silently wrong in the last four digits is worse than one that fails loudly.
 */

// `||` and trimmed, not `??` — an unpassed Docker ARG arrives as "" rather than undefined,
// which `??` accepts and which would silently point every API call at the wrong origin.
const BASE = import.meta.env.VITE_API_URL?.trim() || 'http://localhost:3001'

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

let accessToken: string | null = null

/** Access token is held in memory only — never localStorage, which XSS can read. */
export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

interface Envelope<T> {
  data: T
  meta?: { at: number; cached?: boolean; blockNumber?: string }
}

/**
 * De-duplicated silent refresh.
 *
 * The access token lives 15 minutes and `useAuth` rotates it every 12 — but a
 * laptop that sleeps through that window wakes with a dead token, and then EVERY
 * query 401s until the next tick fires. That looked to the user like "the app
 * logged me out at random".
 *
 * On a 401 we refresh once against the httpOnly cookie and replay the request.
 * The in-flight promise is shared, so ten simultaneous 401s trigger one refresh,
 * not ten competing ones — each of which would rotate the cookie and invalidate
 * the others.
 */
let refreshInFlight: Promise<boolean> | null = null

async function silentRefresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json' },
      })
      if (!res.ok) return false
      const body = (await res.json()) as Envelope<{ accessToken: string }>
      accessToken = body.data.accessToken
      return true
    } catch {
      return false
    } finally {
      // Cleared on the next tick so concurrent callers all observe this result.
      setTimeout(() => {
        refreshInFlight = null
      }, 0)
    }
  })()
  return refreshInFlight
}

async function request<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  const headers = new Headers(init?.headers)
  headers.set('accept', 'application/json')
  if (init?.body) headers.set('content-type', 'application/json')
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`)

  // Encrypt the outgoing body in production. Dev sends plaintext so the Network
  // tab stays readable.
  let body = init?.body
  if (payloadEncryptionEnabled && typeof body === 'string') {
    body = JSON.stringify(await encryptPayload(body))
  }

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    ...(body !== undefined ? { body } : {}),
    headers,
    credentials: 'include', // refresh cookie
  })

  // Refresh-and-replay exactly once. `isRetry` stops a permanently-rejecting
  // endpoint from ping-ponging, and the auth routes are excluded so a failing
  // refresh cannot recurse into itself.
  if (response.status === 401 && !isRetry && !path.startsWith('/api/v1/auth/')) {
    if (await silentRefresh()) return request<T>(path, init, true)
  }

  const text = await response.text()
  let payload: unknown
  try {
    payload = text ? JSON.parse(text) : null
  } catch {
    throw new ApiError('INTERNAL', 'Server returned a malformed response.', response.status)
  }

  // Unwrap the envelope if the server encrypted this response. Detected from the
  // body shape rather than the mode flag, so a client and server that disagree
  // about whether encryption is on still interoperate during a rolling deploy.
  if (looksEncrypted(payload)) {
    const plaintext = await decryptPayload(payload)
    if (plaintext === null) {
      throw new ApiError('INTERNAL', 'Could not decrypt the server response.', response.status)
    }
    try {
      payload = JSON.parse(plaintext)
    } catch {
      throw new ApiError('INTERNAL', 'Server returned a malformed response.', response.status)
    }
  }

  if (!response.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } })?.error
    throw new ApiError(
      err?.code ?? 'INTERNAL',
      err?.message ?? 'Request failed.',
      response.status,
      err?.details,
    )
  }

  return (payload as Envelope<T>).data
}

/** Recursively converts the named fields from decimal strings to bigint. */
function big(value: unknown): bigint {
  if (typeof value === 'bigint') return value
  if (typeof value === 'string' && /^-?\d+$/.test(value)) return BigInt(value)
  if (typeof value === 'number') return BigInt(Math.trunc(value))
  return 0n
}

function points(raw: unknown[]): RoiPoint[] {
  return raw.map((p) => {
    const point = p as { day: number; gross: string; accrued: string }
    return { day: point.day, gross: big(point.gross), accrued: big(point.accrued) }
  })
}

function exit(raw: unknown): ExitQuote {
  const q = raw as Record<string, string> & { mode: 'linear' | 'immediate'; unlockDays: number }
  return {
    mode: q.mode,
    gross: big(q.gross),
    exitTax: big(q.exitTax),
    afterExitTax: big(q.afterExitTax),
    dexTax: big(q.dexTax),
    netUsdt: big(q.netUsdt),
    profit: big(q.profit),
    roi: big(q.roi),
    unlockDays: q.unlockDays,
  }
}

// ─────────────────────────── Protocol ───────────────────────────

export interface ProtocolStats {
  price: { market: bigint; backing: bigint; premiumIndex: bigint; premiumDelta: bigint }
  treasury: { poolUsdt: bigint; poolNdx: bigint; rbsUsdt: bigint; safetyUsdt: bigint; totalBacking: bigint }
  supply: { ndxSupply: bigint; mintHeadroom: bigint }
  stability: { rbsZone: RbsZone; rbsZoneReason: string }
  solvency: {
    lcr: bigint
    status: 'healthy' | 'warning' | 'critical'
    message: string
    scheduledExits30d: bigint
  }
  source: 'rpc' | 'mock'
}

export async function fetchProtocolStats(): Promise<ProtocolStats> {
  const raw = await request<Record<string, Record<string, unknown>>>('/api/v1/protocol/stats')
  return {
    price: {
      market: big(raw.price!.market),
      backing: big(raw.price!.backing),
      premiumIndex: big(raw.price!.premiumIndex),
      premiumDelta: big(raw.price!.premiumDelta),
    },
    treasury: {
      poolUsdt: big(raw.treasury!.poolUsdt),
      poolNdx: big(raw.treasury!.poolNdx),
      rbsUsdt: big(raw.treasury!.rbsUsdt),
      safetyUsdt: big(raw.treasury!.safetyUsdt),
      totalBacking: big(raw.treasury!.totalBacking),
    },
    supply: {
      ndxSupply: big(raw.supply!.ndxSupply),
      mintHeadroom: big(raw.supply!.mintHeadroom),
    },
    stability: {
      rbsZone: raw.stability!.rbsZone as RbsZone,
      rbsZoneReason: String(raw.stability!.rbsZoneReason),
    },
    solvency: {
      lcr: big(raw.solvency!.lcr),
      status: raw.solvency!.status as 'healthy' | 'warning' | 'critical',
      message: String(raw.solvency!.message),
      scheduledExits30d: big(raw.solvency!.scheduledExits30d),
    },
    source: raw.source as unknown as 'rpc' | 'mock',
  }
}

export interface HistoryPoint {
  at: number
  price: bigint
  backing: bigint
  premium: bigint
  lcr: bigint
}

export async function fetchProtocolHistory(hours: number): Promise<HistoryPoint[]> {
  const raw = await request<{ series: Record<string, string | number>[] }>(
    `/api/v1/protocol/history?hours=${hours}`,
  )
  return raw.series.map((p) => ({
    at: Number(p.at),
    price: big(p.price),
    backing: big(p.backing),
    premium: big(p.premium),
    lcr: big(p.lcr),
  }))
}

export interface RbsState {
  band: { lower: bigint; upper: bigint; current: bigint; zone: RbsZone; reason: string }
  reserve: { usdt: bigint; safetyUsdt: bigint }
  price: { market: bigint; backing: bigint }
  recentActions: {
    id: string
    action: 'BUY' | 'MINT'
    usdtAmount: bigint
    ndxAmount: bigint
    premiumIndexAt: bigint
    executedAt: number
    txHash: string
  }[]
}

export async function fetchRbsState(): Promise<RbsState> {
  const raw = await request<Record<string, never>>('/api/v1/rbs/state')
  const r = raw as unknown as {
    band: Record<string, string> & { zone: RbsZone; reason: string }
    reserve: Record<string, string>
    price: Record<string, string>
    recentActions: (Record<string, string> & { action: 'BUY' | 'MINT'; executedAt: number })[]
  }
  return {
    band: {
      lower: big(r.band.lower),
      upper: big(r.band.upper),
      current: big(r.band.current),
      zone: r.band.zone,
      reason: r.band.reason,
    },
    reserve: { usdt: big(r.reserve.usdt), safetyUsdt: big(r.reserve.safetyUsdt) },
    price: { market: big(r.price.market), backing: big(r.price.backing) },
    recentActions: r.recentActions.map((a) => ({
      id: a.id!,
      action: a.action,
      usdtAmount: big(a.usdtAmount),
      ndxAmount: big(a.ndxAmount),
      premiumIndexAt: big(a.premiumIndexAt),
      executedAt: a.executedAt,
      txHash: a.txHash!,
    })),
  }
}

// ─────────────────────────── Genesis ───────────────────────────

export interface GenesisStatus {
  raised: bigint
  softCap: bigint
  hardCap: bigint
  softCapProgress: bigint
  hardCapProgress: bigint
  graduated: boolean
  currentPrice: bigint
  phase: 'raising' | 'ready' | 'graduated'
  quote?: { usdtIn: bigint; ndxOut: bigint; effectivePrice: bigint }
}

export async function fetchGenesisStatus(quoteWei?: bigint): Promise<GenesisStatus> {
  const query = quoteWei !== undefined ? `?quote=${quoteWei}` : ''
  const raw = await request<Record<string, string | boolean | Record<string, string>>>(
    `/api/v1/genesis/status${query}`,
  )
  return {
    raised: big(raw.raised),
    softCap: big(raw.softCap),
    hardCap: big(raw.hardCap),
    softCapProgress: big(raw.softCapProgress),
    hardCapProgress: big(raw.hardCapProgress),
    graduated: Boolean(raw.graduated),
    currentPrice: big(raw.currentPrice),
    phase: raw.phase as 'raising' | 'ready' | 'graduated',
    ...(raw.quote
      ? {
          quote: {
            usdtIn: big((raw.quote as Record<string, string>).usdtIn),
            ndxOut: big((raw.quote as Record<string, string>).ndxOut),
            effectivePrice: big((raw.quote as Record<string, string>).effectivePrice),
          },
        }
      : {}),
  }
}

// ─────────────────────────── Bonds ───────────────────────────

export interface TermsResponse {
  minimumDeposit: bigint
  terms: (BondTerm & { monthlyRateWad: bigint; samplePrincipal: bigint; sampleMilestones: RoiPoint[] })[]
  disclaimer: string
}

export async function fetchBondTerms(): Promise<TermsResponse> {
  const raw = await request<{
    minimumDeposit: string
    terms: (Record<string, string | number | boolean> & { sampleMilestones: unknown[] })[]
    disclaimer: string
  }>('/api/v1/bonds/terms')

  return {
    minimumDeposit: big(raw.minimumDeposit),
    disclaimer: raw.disclaimer,
    terms: raw.terms.map((t) => ({
      id: String(t.id),
      days: Number(t.days),
      dailyRateWad: big(t.dailyRateWad),
      discountBps: Number(t.discountBps),
      active: Boolean(t.active),
      monthlyRateWad: big(t.monthlyRateWad),
      samplePrincipal: big(t.samplePrincipal),
      sampleMilestones: points(t.sampleMilestones),
    })),
  }
}

export interface BondQuote {
  term: BondTerm
  split: {
    deposit: bigint
    marketBuyUsdt: bigint
    lpUsdt: bigint
    estimatedNdxOut: bigint
    priceImpact: bigint
    permanentlyLockedUsdt: bigint
  }
  projection: {
    grossAtMaturity: bigint
    milestones: RoiPoint[]
    schedule: RoiPoint[]
    netLinear: ExitQuote
    exitComparison: { linear: ExitQuote; immediate: ExitQuote; linearAdvantage: bigint }
  }
  market: { ndxPrice: bigint; backingPerNdx: bigint; premiumIndex: bigint }
  constraints: { minimumDeposit: bigint; balance: bigint }
  disclaimer: string
}

export async function quoteBond(input: {
  deposit: bigint
  termDays: number
  slippageBps?: number
  address?: string
}): Promise<BondQuote> {
  const raw = await request<Record<string, Record<string, unknown>>>('/api/v1/bonds/quote', {
    method: 'POST',
    body: JSON.stringify({
      deposit: input.deposit.toString(),
      termDays: input.termDays,
      slippageBps: input.slippageBps ?? 100,
      ...(input.address ? { address: input.address } : {}),
    }),
  })

  const term = raw.term as unknown as Record<string, string | number | boolean>
  const split = raw.split as Record<string, string>
  const projection = raw.projection as Record<string, unknown>
  const market = raw.market as Record<string, string>
  const constraints = raw.constraints as Record<string, string>
  const comparison = projection.exitComparison as Record<string, unknown>

  return {
    term: {
      id: String(term.id),
      days: Number(term.days),
      dailyRateWad: big(term.dailyRateWad),
      discountBps: Number(term.discountBps),
      active: Boolean(term.active),
    },
    split: {
      deposit: big(split.deposit),
      marketBuyUsdt: big(split.marketBuyUsdt),
      lpUsdt: big(split.lpUsdt),
      estimatedNdxOut: big(split.estimatedNdxOut),
      priceImpact: big(split.priceImpact),
      permanentlyLockedUsdt: big(split.permanentlyLockedUsdt),
    },
    projection: {
      grossAtMaturity: big(projection.grossAtMaturity),
      milestones: points(projection.milestones as unknown[]),
      schedule: points(projection.schedule as unknown[]),
      netLinear: exit(projection.netLinear),
      exitComparison: {
        linear: exit(comparison.linear),
        immediate: exit(comparison.immediate),
        linearAdvantage: big(comparison.linearAdvantage),
      },
    },
    market: {
      ndxPrice: big(market.ndxPrice),
      backingPerNdx: big(market.backingPerNdx),
      premiumIndex: big(market.premiumIndex),
    },
    constraints: {
      minimumDeposit: big(constraints.minimumDeposit),
      balance: big(constraints.balance),
    },
    disclaimer: String(raw.disclaimer),
  }
}

// ─────────────────────────── Positions ───────────────────────────

export interface PositionSummary {
  id: string
  principal: bigint
  dailyRateWad: bigint
  termDays: number
  startedAt: number
  maturesAt: number
  status: 'active' | 'matured' | 'unlocking' | 'settled'
  claimed: bigint
  ndxReceived: bigint
  txHash: string
  elapsedDays: number
  remainingDays: number
  gross: bigint
  accrued: bigint
  progress: bigint
  netIfExitedNow: bigint
}

export interface PortfolioResponse {
  positions: PositionSummary[]
  portfolio: {
    totalPrincipal: bigint
    totalGross: bigint
    totalAccrued: bigint
    blendedRoi: bigint
    nextMaturityAt: number | null
  }
}

export async function fetchPositions(address: string): Promise<PortfolioResponse> {
  const raw = await request<{
    positions: Record<string, string | number>[]
    portfolio: Record<string, string | number | null>
  }>(`/api/v1/positions/${address}`)

  return {
    positions: raw.positions.map((p) => ({
      id: String(p.id),
      principal: big(p.principal),
      dailyRateWad: big(p.dailyRateWad),
      termDays: Number(p.termDays),
      startedAt: Number(p.startedAt),
      maturesAt: Number(p.maturesAt),
      status: p.status as PositionSummary['status'],
      claimed: big(p.claimed),
      ndxReceived: big(p.ndxReceived),
      txHash: String(p.txHash),
      elapsedDays: Number(p.elapsedDays),
      remainingDays: Number(p.remainingDays),
      gross: big(p.gross),
      accrued: big(p.accrued),
      progress: big(p.progress),
      netIfExitedNow: big(p.netIfExitedNow),
    })),
    portfolio: {
      totalPrincipal: big(raw.portfolio.totalPrincipal),
      totalGross: big(raw.portfolio.totalGross),
      totalAccrued: big(raw.portfolio.totalAccrued),
      blendedRoi: big(raw.portfolio.blendedRoi),
      nextMaturityAt: raw.portfolio.nextMaturityAt === null ? null : Number(raw.portfolio.nextMaturityAt),
    },
  }
}

export interface PositionDetail extends Omit<PositionValuation, 'position' | 'netIfExitedNow'> {
  position: PositionSummary
  schedule: RoiPoint[]
  milestones: RoiPoint[]
  netIfExitedNow: ExitQuote
  exitComparison: { linear: ExitQuote; immediate: ExitQuote; linearAdvantage: bigint }
}

export async function fetchPositionDetail(address: string, bondId: string): Promise<PositionDetail> {
  const raw = await request<Record<string, unknown>>(`/api/v1/positions/${address}/${bondId}`)
  const position = raw.position as Record<string, string | number>
  const comparison = raw.exitComparison as Record<string, unknown>

  return {
    position: {
      id: String(position.id),
      principal: big(position.principal),
      dailyRateWad: big(position.dailyRateWad),
      termDays: Number(position.termDays),
      startedAt: Number(position.startedAt),
      maturesAt: Number(position.maturesAt),
      status: position.status as PositionSummary['status'],
      claimed: big(position.claimed),
      ndxReceived: big(position.ndxReceived ?? '0'),
      txHash: String(position.txHash),
      elapsedDays: Number(raw.elapsedDays),
      remainingDays: Number(raw.remainingDays),
      gross: big(raw.gross),
      accrued: big(raw.accrued),
      progress: big(raw.progress),
      netIfExitedNow: big((raw.netIfExitedNow as Record<string, string>).netUsdt),
    },
    elapsedDays: Number(raw.elapsedDays),
    remainingDays: Number(raw.remainingDays),
    gross: big(raw.gross),
    accrued: big(raw.accrued),
    progress: big(raw.progress),
    schedule: points(raw.schedule as unknown[]),
    milestones: points(raw.milestones as unknown[]),
    netIfExitedNow: exit(raw.netIfExitedNow),
    exitComparison: {
      linear: exit(comparison.linear),
      immediate: exit(comparison.immediate),
      linearAdvantage: big(comparison.linearAdvantage),
    },
  }
}

// ─────────────────────────── Turbine ───────────────────────────

export interface TurbinePreviewResponse {
  preview: TurbinePreview
  compoundVsClaim: { claimNowNet: bigint; compoundedValue: bigint; costOfClaiming: bigint }
  warning: string
}

export async function previewTurbine(bondId: string): Promise<TurbinePreviewResponse> {
  const raw = await request<Record<string, Record<string, unknown>>>('/api/v1/turbine/preview', {
    method: 'POST',
    body: JSON.stringify({ bondId }),
  })
  const p = raw.preview as Record<string, unknown>
  const c = raw.compoundVsClaim as Record<string, string>

  return {
    preview: {
      claimable: big(p.claimable),
      feeUsdt: big(p.feeUsdt),
      decision: p.decision as 'BUY' | 'MINT',
      reason: String(p.reason),
      premiumIndex: big(p.premiumIndex),
      ndxOut: big(p.ndxOut),
      queueReadyAt: Number(p.queueReadyAt),
    },
    compoundVsClaim: {
      claimNowNet: big(c.claimNowNet),
      compoundedValue: big(c.compoundedValue),
      costOfClaiming: big(c.costOfClaiming),
    },
    warning: String(raw.warning),
  }
}

export interface QueueEntryResponse {
  id: string
  bondId: string | null
  claimable: bigint
  feeUsdt: bigint
  ndxOut: bigint
  decision: 'BUY' | 'MINT'
  premiumIndexAt: bigint
  queuedAt: number
  readyAt: number
  settledAt: number | null
  txHash: string
  ready: boolean
  secondsRemaining: number
  progress: number
}

export async function fetchQueue(address: string): Promise<{
  queueDelaySeconds: number
  serverTime: number
  entries: QueueEntryResponse[]
  pending: number
  readyToSettle: number
}> {
  const raw = await request<{
    queueDelaySeconds: number
    serverTime: number
    entries: Record<string, string | number | boolean | null>[]
    pending: number
    readyToSettle: number
  }>(`/api/v1/turbine/queue/${address}`)

  return {
    queueDelaySeconds: raw.queueDelaySeconds,
    serverTime: raw.serverTime,
    pending: raw.pending,
    readyToSettle: raw.readyToSettle,
    entries: raw.entries.map((e) => ({
      id: String(e.id),
      bondId: e.bondId === null ? null : String(e.bondId),
      claimable: big(e.claimable),
      feeUsdt: big(e.feeUsdt),
      ndxOut: big(e.ndxOut),
      decision: e.decision as 'BUY' | 'MINT',
      premiumIndexAt: big(e.premiumIndexAt),
      queuedAt: Number(e.queuedAt),
      readyAt: Number(e.readyAt),
      settledAt: e.settledAt === null ? null : Number(e.settledAt),
      txHash: String(e.txHash),
      ready: Boolean(e.ready),
      secondsRemaining: Number(e.secondsRemaining),
      progress: Number(e.progress),
    })),
  }
}

// ─────────────────────────── Auth ───────────────────────────

export async function fetchNonce(): Promise<{
  nonce: string
  domain: string
  statement: string
  chainId: number
}> {
  return request('/api/v1/auth/nonce')
}

/**
 * The sign-in response now carries referral state.
 *
 * It rides along on verify rather than being fetched separately because this is the
 * one request the user is definitely waiting on — a second round trip to decide
 * whether to show the first-time prompt would make the modal arrive after the
 * dashboard had already painted.
 */
export interface SessionResponse {
  address: string
  isAdmin: boolean
  accessToken: string
  expiresInSeconds: number
  /** This wallet's own code, to share. Generated at first sign-in. */
  referralCode: string | null
  /**
   * True while attribution is unsettled — drives the first-time modal.
   *
   * The only "is this new" signal, and the right one: it stays true until the user
   * actually answers, so someone who closed the tab mid-flow is asked again.
   */
  needsReferral: boolean
}

export async function verifySignature(
  message: string,
  signature: string,
): Promise<SessionResponse> {
  return request('/api/v1/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ message, signature }),
  })
}

export async function refreshSession(): Promise<{
  address: string
  isAdmin: boolean
  accessToken: string
  /*
   * Present on refresh too. A returning user's session is restored from the cookie
   * without a signature, so `verify` never runs — without these the prompt would be
   * skipped for exactly the users who closed the tab before answering it.
   */
  needsReferral?: boolean
  referralCode?: string | null
}> {
  return request('/api/v1/auth/refresh', { method: 'POST' })
}

// ─────────────────────────── Referrals ───────────────────────────

export interface ReferralStanding {
  /** This wallet's own code, to share. */
  code: string | null
  /** True while the first-time prompt is still owed. */
  needsPrompt: boolean
  /**
   * How attribution resolved. Derived server-side from the upline and the bind
   * timestamp — 'user' when a real wallet referred them, 'house' when they declined,
   * null when they have not been asked.
   */
  referredBySource: 'user' | 'house' | null
  /** The upline wallet, or null. Null with source 'house' is not missing data. */
  referrerAddress: string | null
  boundAt: string | null
  /** Direct referrals — wallets whose upline is this one. */
  downlineCount: number
}

export async function fetchReferralStanding(): Promise<ReferralStanding> {
  return request('/api/v1/referrals/me')
}

/**
 * Answer the first-time prompt.
 *
 * The server requires a real code belonging to another Ninja wallet. Missing, house,
 * unknown, and self-referral codes leave the wallet gated.
 */
export async function bindReferral(code: string): Promise<ReferralStanding> {
  return request('/api/v1/referrals/bind', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

/**
 * The destination that opens the partner panel already signed in.
 *
 * ── Why the URL is asked for rather than built here ──
 * It carries a token signed with a secret shared with the partner, and that secret must
 * never reach a browser. The server mints the whole URL from the CALLER's session, so a
 * client cannot request a hand-off for a wallet other than its own — which matters
 * because on the partner's side an address alone opens a session.
 */
/** One earnings record, as our backend normalises it from the partner. */
export interface PartnerIncomeRow {
  /** ISO 8601, or null when the partner's own date could not be read. */
  at: string | null
  amount: string
  /** "Direct", "ROI", "Level", … */
  type: string
  transactionId: string | null
}

export interface PartnerIncomeSummary {
  /**
   * The partner's own total, passed through untouched.
   *
   * Not re-derived from `rows`: these are money figures, and a rounding difference of
   * ours contradicting the number they consider authoritative is worse than trusting it.
   */
  total: string
  rows: PartnerIncomeRow[]
}

/** One credit in the income wallet, as our own ledger records it. */
export interface IncomeEntry {
  id: string
  /** Raw bNDX, 18 decimals — a uint256 does not survive a JSON number. */
  amount: string
  status: string
  reversedReason: string | null
  at: string
  reference: string
}

/**
 * The income wallet: bNDX credited by partners, held here, and withdrawable.
 *
 * Distinct from `partnerIncome`, which reads the PARTNER's own USDT ledger and is
 * informational. This one is spendable, and is charged a different withdrawal fee from
 * the deposit balance — which is why the two are never summed.
 */
export interface IncomeWallet {
  available: string
  credited: string
  withdrawn: string
  feesPaid: string
  entryCount: number
  entries: IncomeEntry[]
}

export async function incomeWallet(): Promise<IncomeWallet> {
  return request('/api/v1/income/me')
}

export interface IncomeWithdrawalIntent {
  id: string
  status: 'created' | 'authorized' | 'amount_received' | 'payment_detected' | 'confirming' | 'settling' | 'confirmed' | 'expired' | 'cancelled' | 'failed'
  incomeAmountUsdt: string
  feeAmountUsdt: string
  receivedFeeAmountUsdt: string
  carriedFeeAmountUsdt: string
  remainingFeeAmountUsdt: string
  paymentAddress: string
  token: { symbol: 'USDT'; address: string; decimals: number }
  chainId: number
  deadline: string
  settlementTxHash: string | null
  bndxCredited: string | null
  priceWad: string | null
  error: string | null
}

export function createIncomeWithdrawal(amountUsdt: bigint): Promise<IncomeWithdrawalIntent> {
  return request('/api/v1/income-withdrawals', { method: 'POST', body: JSON.stringify({ amountUsdt: amountUsdt.toString() }) })
}
export function authorizeIncomeWithdrawal(id: string, signature: string): Promise<IncomeWithdrawalIntent> {
  return request(`/api/v1/income-withdrawals/${id}/authorize`, { method: 'POST', body: JSON.stringify({ signature }) })
}
export function getIncomeWithdrawal(id: string): Promise<IncomeWithdrawalIntent> { return request(`/api/v1/income-withdrawals/${id}`) }
export function discardIncomeWithdrawal(id: string): Promise<IncomeWithdrawalIntent> { return request(`/api/v1/income-withdrawals/${id}/discard`, { method: 'POST' }) }
export function getActiveIncomeWithdrawal(): Promise<IncomeWithdrawalIntent | null> { return request('/api/v1/income-withdrawals/active') }
export function getIncomeWithdrawalVault(): Promise<{ bndxBalance: string; incomeTotalUsdt: string; incomeAvailableUsdt: string; token: 'bNDX' }> { return request('/api/v1/income-withdrawals/vault') }

export interface DbBndxSwapIntent {
  id: string
  status:
    | 'created'
    | 'registering'
    | 'pending_admin_approval'
    | 'approved'
    | 'cancellation_pending'
    | 'awaiting_wallet_payment'
    | 'payout_submitted'
    | 'held'
    | 'sending'
    | 'sent'
    | 'completed'
    | 'rejected'
    | 'failed'
    | 'cancelled'
    | 'stuck'
  bndxAmount: string
  grossUsdtAmount: string
  sellTaxUsdtAmount: string
  usdtAmount: string
  priceWad: string
  minimumUsdtOut: string
  txHash: string | null
  reason: string | null
  destination: string
  onchainIntentId: `0x${string}` | null
  registrationTxHash: `0x${string}` | null
  cancellationTxHash: `0x${string}` | null
  payerAddress: `0x${string}` | null
  executionDeadline: string | null
  maxUsdtPayment: string | null
}
export interface TurbineTypedData {
  domain: { name: string; version: string; chainId: number; verifyingContract: `0x${string}` }
  primaryType: 'SwapIntent' | 'CancelIntent'
  types: Record<string, readonly { name: string; type: string }[]>
  message: Record<string, string>
}
export function createDbBndxSwap(bndxAmount: bigint, idempotencyKey: string): Promise<DbBndxSwapIntent> { return request('/api/v1/db-bndx-swaps', { method: 'POST', body: JSON.stringify({ bndxAmount: bndxAmount.toString(), idempotencyKey }) }) }
export function getDbBndxSwapAuthorization(id: string): Promise<TurbineTypedData> { return request(`/api/v1/db-bndx-swaps/${id}/authorization`) }
export function authorizeDbBndxSwap(id: string, signature: string): Promise<DbBndxSwapIntent> { return request(`/api/v1/db-bndx-swaps/${id}/authorize`, { method: 'POST', body: JSON.stringify({ signature }) }) }
export function getDbBndxSwap(id: string): Promise<DbBndxSwapIntent> { return request(`/api/v1/db-bndx-swaps/${id}`) }
export function getActiveDbBndxSwap(): Promise<DbBndxSwapIntent | null> { return request('/api/v1/db-bndx-swaps/active') }
export function getDbBndxSwapCancellationAuthorization(id: string): Promise<TurbineTypedData> { return request(`/api/v1/db-bndx-swaps/${id}/cancellation-authorization`) }
export function cancelDbBndxSwap(id: string, signature?: string): Promise<DbBndxSwapIntent> { return request(`/api/v1/db-bndx-swaps/${id}/cancel`, { method: 'POST', body: JSON.stringify(signature ? { signature } : {}) }) }
export function reconcileDbBndxSwapCancellation(id: string, txHash: `0x${string}`): Promise<DbBndxSwapIntent> { return request(`/api/v1/db-bndx-swaps/${id}/cancellation-transaction`, { method: 'POST', body: JSON.stringify({ txHash }) }) }
export function getDbBndxSwapVault(): Promise<{ bndxBalance: string; token: 'bNDX'; sellTaxBps: number; incomeLimitUsdt: string; incomeRedeemedUsdt: string; incomeRemainingUsdt: string; swapCapRemainingUsdt: string; minimumSwapUsdt?: string }> { return request('/api/v1/db-bndx-swaps/vault') }

/** All earnings for the signed-in wallet. Narrowed to them server-side. */
export async function partnerIncome(): Promise<PartnerIncomeSummary> {
  return request('/api/v1/partner-panel/income')
}

/** ROI only. */
export async function partnerRoi(): Promise<PartnerIncomeSummary> {
  return request('/api/v1/partner-panel/roi')
}

export async function partnerPanelHandoff(): Promise<{ url: string }> {
  return request('/api/v1/partner-panel/handoff')
}

/**
 * Make sure this wallet exists at the partner before signing in.
 *
 * Binding a referral only enqueues the upstream registration, so a brand-new user can reach
 * the panel before the partner has an account for them — and the partner's answer to that is
 * a login form, which reads as a bug rather than as "one moment". This delivers that user's
 * registration and reports whether it landed.
 */
export async function partnerPanelEnsure(): Promise<{ ready: boolean }> {
  return request('/api/v1/partner-panel/ensure', { method: 'POST' })
}

/** Whether the panel is reachable at all, so the entry point can be hidden when it is not. */
export async function partnerPanelEnabled(): Promise<{ enabled: boolean }> {
  return request('/api/v1/partner-panel/status')
}

export async function logout(): Promise<void> {
  await request('/api/v1/auth/logout', { method: 'POST' })
  setAccessToken(null)
}

// ─────────────────────────── Deposit address ───────────────────────────

export interface DepositAddressInfo {
  /** Where the user sends funds. */
  address: string
  token: { symbol: string; address: string; decimals: number }
  chainId: number
  createdAt: string
  /** True when this call created the address rather than returning an existing one. */
  issued: boolean
}

/**
 * Issue (or fetch) the caller's deposit address.
 *
 * Idempotent server-side, so the button behind this is safe to double-click — a second
 * call returns the same address rather than stranding whatever was sent to the first.
 */
export async function issueDepositAddress(address?: string): Promise<DepositAddressInfo> {
  /*
   * `address` is only read when there is no session. Once signed in, the server uses
   * the wallet from the token and ignores the body — a proved identity must not be
   * overridable by a parameter.
   */
  return request('/api/v1/deposits/address', {
    method: 'POST',
    ...(address ? { body: JSON.stringify({ address }) } : {}),
  })
}

// ─────────────────────────── Bond payment intents ───────────────────────────

export interface BondPaymentIntent {
  id: string
  status: 'created' | 'authorized' | 'amount_received' | 'payment_detected' | 'confirming' | 'settling' | 'confirmed' | 'expired' | 'failed'
  authorization: {
    beneficiary: `0x${string}`
    payer: `0x${string}`
    offeringId: bigint
    paymentToken: `0x${string}`
    paymentAmount: bigint
    minPayoutAmount: bigint
    paymentReference: `0x${string}`
    nonce: bigint
    deadline: bigint
  }
  typedData: {
    domain: { name: string; version: string; chainId: number; verifyingContract: `0x${string}` }
    types: Record<string, readonly { name: string; type: string }[]>
    primaryType: 'PurchaseAuthorization'
    message: Record<string, unknown>
  }
  payment: null | { chainId: number; token: `0x${string}`; address: `0x${string}`; amount: bigint; uri: string }
  paymentTxHash: `0x${string}` | null
  receivedPaymentAmount: string | null
  /** USDT retained from an expired payment window and applied to this request. */
  carriedForwardPaymentAmount: string | null
  minimumPaymentAmount: string
  remainingPaymentAmount: string
  /** Amount still needed to reach the user's originally requested order amount. */
  remainingRequestedPaymentAmount: string
  settlementTxHash: `0x${string}` | null
  /** Genesis Curve recorder transaction, when this is a Genesis Bond purchase. */
  genesisCurveTxHash: `0x${string}` | null
  product: 'bond' | 'genesis'
  /** Estimated before payment; final allocation once the intent is confirmed. */
  genesisBondAmount: string | null
  bondId: string | null
}

function parseBondIntent(raw: unknown): BondPaymentIntent {
  const row = raw as BondPaymentIntent & { authorization: Record<string, unknown>; payment: Record<string, unknown> | null }
  const authorization = row.authorization
  return {
    ...row,
    authorization: {
      ...authorization,
      offeringId: big(authorization.offeringId),
      paymentAmount: big(authorization.paymentAmount),
      minPayoutAmount: big(authorization.minPayoutAmount),
      nonce: big(authorization.nonce),
      deadline: big(authorization.deadline),
    } as BondPaymentIntent['authorization'],
    typedData: {
      ...row.typedData,
      message: {
        ...row.typedData.message,
        offeringId: big(row.typedData.message.offeringId),
        paymentAmount: big(row.typedData.message.paymentAmount),
        minPayoutAmount: big(row.typedData.message.minPayoutAmount),
        nonce: big(row.typedData.message.nonce),
        deadline: big(row.typedData.message.deadline),
      },
    },
    payment: row.payment ? { ...row.payment, amount: big(row.payment.amount) } as BondPaymentIntent['payment'] : null,
  }
}

export async function createBondPaymentIntent(input: { offeringId: bigint; paymentAmount: bigint; minPayoutAmount: bigint; product?: 'bond' | 'genesis'; genesisBondAmount?: bigint }) {
  return parseBondIntent(await request('/api/v1/bond-payment-intents', {
    method: 'POST',
    body: JSON.stringify({
      offeringId: input.offeringId.toString(),
      paymentAmount: input.paymentAmount.toString(),
      minPayoutAmount: input.minPayoutAmount.toString(),
      ...(input.product ? { product: input.product } : {}),
      ...(input.genesisBondAmount ? { genesisBondAmount: input.genesisBondAmount.toString() } : {}),
    }),
  }))
}

export async function authorizeBondPaymentIntent(id: string, signature: `0x${string}`) {
  return parseBondIntent(await request(`/api/v1/bond-payment-intents/${id}/authorize`, {
    method: 'POST',
    body: JSON.stringify({ signature }),
  }))
}

export async function acceptReceivedBondPaymentAmount(id: string) {
  return parseBondIntent(await request(`/api/v1/bond-payment-intents/${id}/accept-received-amount`, {
    method: 'POST',
  }))
}

export async function discardBondPaymentIntent(id: string) {
  return parseBondIntent(await request(`/api/v1/bond-payment-intents/${id}/discard`, { method: 'POST' }))
}

export async function getBondPaymentIntent(id: string) {
  return parseBondIntent(await request(`/api/v1/bond-payment-intents/${id}`))
}

export async function getActiveBondPaymentIntent() {
  const active = await request('/api/v1/bond-payment-intents/active')
  return active ? parseBondIntent(active) : null
}

export interface GenesisBondPurchase {
  id: string
  paymentAmount: bigint
  bondAmount: bigint
  /** ERC-721 identity minted for this purchase. Null only while an older row is being reconciled. */
  tokenId: string | null
  certificateAddress: `0x${string}`
  purchasedAt: string
  treasuryTxHash: `0x${string}` | null
  curveTxHash: `0x${string}` | null
}

type GenesisBondPurchaseRaw = Omit<GenesisBondPurchase, 'paymentAmount' | 'bondAmount'> & {
  paymentAmount: string
  bondAmount: string
}

/** Completed Genesis Curve purchases for the currently signed-in wallet. */
export async function fetchGenesisBondPurchases(): Promise<GenesisBondPurchase[]> {
  const rows = await request<GenesisBondPurchaseRaw[]>('/api/v1/bond-payment-intents/history/genesis')
  return rows.map((row) => ({
    ...row,
    paymentAmount: big(row.paymentAmount),
    bondAmount: big(row.bondAmount),
  }))
}

export interface GenesisCurveDay {
  day: string
  sold: bigint
  collected: bigint
  cumulativeSold: bigint
  cumulativeCollected: bigint
  startPrice: bigint
  endPrice: bigint
}

export interface GenesisCurveHistory {
  days: GenesisCurveDay[]
  bondsIssued: number | null
}

/** Daily Genesis Curve purchase history, aggregated by the backend from recorded sales. */
export async function fetchGenesisCurveHistory(): Promise<GenesisCurveHistory> {
  const raw = await request<{
    days: Array<Omit<GenesisCurveDay, 'sold' | 'collected' | 'cumulativeSold' | 'cumulativeCollected' | 'startPrice' | 'endPrice'> & Record<'sold' | 'collected' | 'cumulativeSold' | 'cumulativeCollected' | 'startPrice' | 'endPrice', string>>
    bondsIssued?: number
  }>('/api/v1/genesis/history')
  return {
    days: raw.days.map((day) => ({
      ...day,
      sold: big(day.sold),
      collected: big(day.collected),
      cumulativeSold: big(day.cumulativeSold),
      cumulativeCollected: big(day.cumulativeCollected),
      startPrice: big(day.startPrice),
      endPrice: big(day.endPrice),
    })),
    // Optional during a rolling backend/frontend deployment.
    bondsIssued: raw.bondsIssued === undefined ? null : Number(raw.bondsIssued),
  }
}

// ─────────────────────────── Withdrawals ───────────────────────────

export interface WithdrawalRecord {
  id: string
  /**
   * Which balance it drew from, and therefore which rate `fee` reflects:
   * `deposit` is 2.5%, `income` is 10%.
   */
  source: 'deposit' | 'income'
  /** Gross bNDX — what leaves the balance. */
  amount: string
  /** The protocol's cut, in bNDX. The rate depends on `source`. */
  fee: string
  /** What is actually transferred on chain: amount − fee. */
  net: string
  to: string
  status:
    | 'requested'
    | 'approved'
    | 'sending'
    | 'sent'
    | 'completed'
    | 'rejected'
    | 'failed'
    | 'stuck'
  txHash: string | null
  reason: string | null
  requestedAt: string
  completedAt: string | null
}

export async function fetchWithdrawals(): Promise<{
  /** Withdrawable bNDX — what completed deposits actually bought, minus what is out. */
  available: string
  token: string
  /**
   * Partner-reported income, GROSS.
   *
   * Separate from `available` rather than added to it because the two are charged
   * differently — 2.5% on deposit balance, 10% on income — so a single combined number
   * could not be quoted a fee.
   */
  income: {
    available: string
    credited: string
    /** 1000 = 10%. Sent by the server so the UI never hard-codes a rate that can change. */
    feeBps: number
    token: string
  }
  withdrawals: WithdrawalRecord[]
}> {
  return request('/api/v1/withdrawals')
}

/**
 * Request a withdrawal.
 *
 * A 200 means RECORDED, not paid — settlement is a separate step server-side. The UI
 * must not tell the user their funds have been sent on the strength of this response.
 */
export async function requestWithdrawal(input: {
  amount: bigint
  idempotencyKey?: string
  /** Which balance to draw from. Omitted means `deposit`, exactly as before. */
  source?: 'deposit' | 'income'
}): Promise<{
  id: string
  amount: string
  fee: string
  net: string
  token: string
  to: string
  status: string
  note: string
}> {
  /*
   * No destination is sent, and the server has no parameter for one — funds always
   * return to the wallet that created the account. The server rejects unknown fields
   * outright, so adding a `to` here would fail the request rather than be ignored.
   */
  return request('/api/v1/withdrawals', {
    method: 'POST',
    body: JSON.stringify({
      amount: input.amount.toString(),
      ...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}),
      ...(input.source ? { source: input.source } : {}),
    }),
  })
}

// ─────────────────────────── Lending activity ───────────────────────────

/**
 * Wire kind → the vocabulary the UI already speaks.
 *
 * The database column is snake_case because that is the house style for Postgres
 * enums; the components were written against the chain reader's camelCase. Rather
 * than teach every icon, tone and label map to answer to two spellings, the two are
 * reconciled once, here — so a row from the indexer and a row from the fallback are
 * indistinguishable downstream.
 */
const KIND_ALIASES: Record<string, string> = {
  supply_withdrawn: 'supplyWithdrawn',
}

export interface ActivityRow {
  id: string
  side: 'lend' | 'borrow' | 'multiply'
  kind: string
  amount: bigint
  unit: 'ndx' | 'payment'
  detail: Record<string, string> | null
  /** Unix seconds. */
  at: number
  blockNumber: bigint
  txHash: `0x${string}`
  logIndex: number
}

export interface ActivityPage {
  entries: ActivityRow[]
  /** Opaque — pass back verbatim for the next page. `null` means this is the last. */
  nextCursor: string | null
  /** Only present on the first page; the client keeps it for the pager. */
  total: number | null
}

/**
 * A wallet's lending history, from the indexer.
 *
 * Keyset-paginated: the cursor carries the last row's position rather than an offset,
 * so a deep page costs what the first page costs. Amounts arrive as decimal strings
 * and are converted to bigint here, at the boundary — a uint256 does not survive a
 * double, and a history that is quietly wrong in its last digits is worse than one
 * that fails loudly.
 */
export async function fetchLendActivity(input: {
  address: string
  side?: 'lend' | 'borrow' | 'multiply'
  limit?: number
  cursor?: string | null
}): Promise<ActivityPage> {
  const params = new URLSearchParams()
  if (input.side) params.set('side', input.side)
  if (input.limit) params.set('limit', String(input.limit))
  if (input.cursor) params.set('cursor', input.cursor)
  const query = params.toString()

  const raw = await request<{
    entries: Record<string, unknown>[]
    nextCursor: string | null
    total: number | null
  }>(`/api/v1/activity/${input.address}${query ? `?${query}` : ''}`)

  return {
    entries: raw.entries.map((e) => ({
      id: String(e.id),
      side: e.side as ActivityRow['side'],
      kind: KIND_ALIASES[String(e.kind)] ?? String(e.kind),
      amount: big(e.amount as string),
      unit: e.unit as ActivityRow['unit'],
      detail: (e.detail as Record<string, string> | null) ?? null,
      at: Math.floor(new Date(String(e.at)).getTime() / 1000),
      blockNumber: big(e.blockNumber as string),
      txHash: String(e.txHash) as `0x${string}`,
      logIndex: Number(e.logIndex),
    })),
    nextCursor: raw.nextCursor,
    total: raw.total,
  }
}
